import { useState } from 'react';

import {
  Box,
  Divider,
  Form,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Modal,
  Paragraph,
  Select,
  Text,
  Textarea,
} from '@zillow/constellation-10';

import { RentalManagerUserFeedClaimRequestType } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserFeedClaimsV2WebApiHandler_request } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useAddressAutocomplete } from '../../../hooks/queries/address';

import { useAuth } from '../../AuthProvider';
import AutoComplete from '../../Autocomplete';
import IntermediateStep from '../../IntermediateStep';
import ModalFooter from '../ModalFooter';

import type { ChangeEvent, FormEvent } from 'react';
import type { DatarouterError } from '@zg-rentals/web-base';
type FormState = {
  email: string;
  requestType?: RentalManagerUserFeedClaimRequestType;
  fullAddress?: string;
  unitNumber?: string;
  companyName?: string;
  feedProviderName?: string;
  feedOrCompanyId?: string;
  note?: string;
};

type CompleteFormState = FormState & { requestType: RentalManagerUserFeedClaimRequestType };

const OPTIONS = {
  [RentalManagerUserFeedClaimRequestType.ADD_NEW_FEED]: {
    value: RentalManagerUserFeedClaimRequestType.ADD_NEW_FEED,
    name: 'Add a new feed',
    addressRequired: false,
  },
  [RentalManagerUserFeedClaimRequestType.SWITCH_FEED]: {
    value: RentalManagerUserFeedClaimRequestType.SWITCH_FEED,
    name: 'Switch or disconnect feed',
    addressRequired: true,
  },
  [RentalManagerUserFeedClaimRequestType.ADD_INACTIVE_LISTING]: {
    value: RentalManagerUserFeedClaimRequestType.ADD_INACTIVE_LISTING,
    name: 'Add a property that is inactive or not displaying from your feed',
    addressRequired: true,
  },
  [RentalManagerUserFeedClaimRequestType.ADD_NEW_LISTING]: {
    value: RentalManagerUserFeedClaimRequestType.ADD_NEW_LISTING,
    name: 'Add additional or new properties',
    addressRequired: true,
  },
  [RentalManagerUserFeedClaimRequestType.OTHER]: {
    value: RentalManagerUserFeedClaimRequestType.OTHER,
    name: 'Other',
    addressRequired: true,
  },
} as Record<
  RentalManagerUserFeedClaimRequestType,
  { value: RentalManagerUserFeedClaimRequestType; name: string; addressRequired: boolean }
>;

const successText =
  'A Zillow Rentals representative is working on this request and will get back to you soon. If you’ve requested new feed listings to be added to your account, confirm that the listings are being actively syndicated to Zillow and that they contain pricing, photos and other required information.';

const AddFeedListingsModal = ({ onClose }: { onClose: () => void }) => {
  const { user } = useAuth();
  const email = user?.email || '';

  const [form, setForm] = useState<FormState>({ email });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [setPartialAddress, queryData] = useAddressAutocomplete();

  const { mutateAsync: requestFeedClaim } = useRentalManagerUserFeedClaimsV2WebApiHandler_request({
    meta: {
      context: {
        api: 'rental-manager',
        details: 'Requesting feed claim from add feed listings modal',
      },
    },
  });

  const handleAddressChange = (value: string) => {
    handleUpdateForm('fullAddress', value);
    setPartialAddress(value);
  };

  const handleUpdateForm = (key: keyof FormState, value: string) => {
    setForm({
      ...form,
      [key]: value,
    });
  };

  const isCompleteFormState = (form: FormState): form is CompleteFormState => {
    if (!form.requestType) return false;
    return !OPTIONS[form.requestType].addressRequired || !!form.fullAddress;
  };

  const handleSubmit = (e?: unknown) => {
    if (e && typeof e === 'object' && 'preventDefault' in e) {
      (e as FormEvent).preventDefault();
    }
    setIsSubmitting(true);
    setErrorMessage(null);

    if (isCompleteFormState(form)) {
      requestFeedClaim({ postData: form })
        .then(() => {
          setIsSuccess(true);
          setIsSubmitting(false);
        })
        .finally(() => {
          setIsSubmitting(false);
        })
        .catch((error: DatarouterError) => {
          setErrorMessage(error.message);
          setIsSubmitting(false);
        });
    }
  };

  const renderAddressFields = () => (
    <>
      <Label required={true}>Example property address</Label>
      <Box css={{ paddingBottom: '1200', marginBottom: '200' }}>
        <AutoComplete
          className=""
          items={queryData?.data?.addresses}
          placeholder=""
          onChange={handleAddressChange}
          onSelect={(newAddress: { name: string }) => handleUpdateForm('fullAddress', newAddress.name)}
          value={form.fullAddress || ''}
        />
      </Box>
      <FormField
        label={<Label>Unit number</Label>}
        control={
          <Input
            value={form.unitNumber || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpdateForm('unitNumber', e.target.value)}
          />
        }
        description={<FormHelp>If the property doesn’t have a unit number, leave this field blank</FormHelp>}
      />
    </>
  );

  const renderSupplementalInformation = () => (
    <>
      <Divider css={{ marginBlock: '400' }} />
      <Text textStyle="body-bold">Supplemental information</Text>
      <Paragraph css={{ paddingBottom: '600', paddingTop: '200' }}>
        Providing additional information will help our support team look into your request faster.
      </Paragraph>
      <FormField
        label={<Label>Name of company</Label>}
        control={
          <Input
            value={form.companyName || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpdateForm('companyName', e.target.value)}
          />
        }
      />
      <FormField
        label={<Label>Name of the feed provider</Label>}
        control={
          <Input
            value={form.feedProviderName || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpdateForm('feedProviderName', e.target.value)}
          />
        }
      />
      <FormField
        label={<Label>Feed ID / Company ID</Label>}
        control={
          <Input
            value={form.feedOrCompanyId || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpdateForm('feedOrCompanyId', e.target.value)}
          />
        }
      />
      <FormField
        label={<Label>Email address</Label>}
        control={
          <Input
            value={form.email || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpdateForm('email', e.target.value)}
            className="fs-mask"
          />
        }
      />
      <FormField
        label={<Label>Note</Label>}
        control={
          <Textarea
            value={form.note || ''}
            placeholder="Tell us any specific things we should know about"
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleUpdateForm('note', e.target.value)}
          />
        }
      />
    </>
  );

  const renderForm = () => (
    <Form asChild={true} onSubmit={handleSubmit}>
      <div>
        <Paragraph css={{ paddingBottom: '600' }}>
          Looking to add more feed listings? Tell us about the situation and we will connect you to our
          support/onboarding team!
        </Paragraph>

        <FormField
          required={true}
          label={<Label>What would you like to add?</Label>}
          control={
            <Select
              aria-label="RequestType"
              name="requestType"
              value={form.requestType || ''}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => handleUpdateForm('requestType', e.target.value)}
            >
              <option disabled={!!form.requestType}>Please select</option>
              {Object.values(RentalManagerUserFeedClaimRequestType).map((key) => (
                <option key={OPTIONS[key].value} value={OPTIONS[key].value}>
                  {OPTIONS[key].name}
                </option>
              ))}
            </Select>
          }
        />

        {form.requestType && OPTIONS[form.requestType].addressRequired && renderAddressFields()}
        {form.requestType && renderSupplementalInformation()}
      </div>
    </Form>
  );

  return (
    <Modal
      size="md"
      header={<Heading level={2}>Add feed listings</Heading>}
      open={true}
      onOpenChange={(open) => open === false && onClose()}
      footer={
        !isSuccess && (
          <ModalFooter
            hasError={!!errorMessage}
            errorMessage={errorMessage}
            isSubmitting={isSubmitting}
            isSubmitDisabled={!isCompleteFormState(form)}
            submitText="Submit request"
            onCancel={onClose}
            onSubmit={handleSubmit}
          />
        )
      }
      body={
        isSuccess ? (
          <IntermediateStep headingLevel={3} headingText="Your request was submitted">
            <Paragraph>{successText}</Paragraph>
          </IntermediateStep>
        ) : (
          renderForm()
        )
      }
    />
  );
};
export default AddFeedListingsModal;
